import { LitElement, html, css, nothing } from 'lit';
import { generationChanged } from '../utils/constants.js';
import { customElement, property, state, query } from 'lit/decorators.js';
import { baseStyles, panelStyles, badgeStyles } from '../styles/shared.js';
import type { ChatbotController } from '../controllers/chatbot-controller.js';
import type { ModelerController } from '../controllers/modeler-controller.js';
import type { ChatMessage } from '../api/types.js';

// ---------------------------------------------------------------------------
// <sysml-chatbot-panel> -- AI chatbot panel with full feature set
//
// Ported from: SysmlChatbotPanel.vue + sysmlChatbotStore.ts
//
// Features:
// - Completeness score bar (animated width transition)
// - Quality score with color-coded background
// - Trace coverage percentage
// - Phase indicator (exploring -> ready -> generating)
// - Message thread with user/assistant bubbles
// - Code suggestion box with Apply/Dismiss buttons
// - Quick action buttons
// ---------------------------------------------------------------------------

@customElement('sysml-chatbot-panel')
export class SysmlChatbotPanel extends LitElement {
  static override styles = [
    baseStyles,
    panelStyles,
    badgeStyles,
    css`
      :host { display: flex; flex-direction: column; height: 100%; }

      /* -- Score dashboard -- */
      .score-dashboard {
        display: flex;
        flex-direction: column;
        gap: var(--r5-space-1);
        padding: var(--r5-space-2) var(--r5-space-3);
        border-bottom: 1px solid var(--r5-border);
        background: var(--r5-surface-muted);
      }
      .score-row {
        display: flex;
        align-items: center;
        gap: var(--r5-space-2);
        font-size: var(--r5-text-xs);
      }
      .score-label {
        min-width: 80px;
        color: var(--r5-text-muted);
        font-weight: 500;
      }
      .score-track {
        flex: 1;
        height: 6px;
        border-radius: 3px;
        background: var(--r5-border);
        overflow: hidden;
      }
      .score-fill {
        height: 100%;
        border-radius: 3px;
        transition: width 0.6s var(--r5-ease-default);
      }
      .score-fill.success { background: var(--r5-success-500); }
      .score-fill.warning { background: var(--r5-warning-500); }
      .score-fill.error { background: var(--r5-destructive-500); }
      .score-value {
        min-width: 36px;
        text-align: right;
        font-weight: 600;
        font-variant-numeric: tabular-nums;
      }
      .quality-badge {
        display: inline-flex;
        padding: 2px var(--r5-space-2);
        border-radius: var(--r5-radius-full);
        font-size: var(--r5-font-size-badge);
        font-weight: 600;
        white-space: nowrap;
      }
      .quality-badge.good { background: var(--r5-success-50); color: var(--r5-success-700); }
      .quality-badge.fair { background: var(--r5-warning-50); color: var(--r5-warning-700); }
      .quality-badge.poor { background: var(--r5-destructive-50); color: var(--r5-destructive-700); }

      /* -- Phase indicator -- */
      .phase-indicator {
        display: flex;
        align-items: center;
        gap: var(--r5-space-1);
        padding: var(--r5-space-1) var(--r5-space-3);
        font-size: var(--r5-text-xs);
        color: var(--r5-text-muted);
        border-bottom: 1px solid var(--r5-border);
      }
      .phase-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        flex-shrink: 0;
      }
      .phase-dot.exploring { background: var(--r5-warning-500); animation: pulse-dot 1.5s ease infinite; }
      .phase-dot.ready { background: var(--r5-success-500); }
      .phase-dot.generating { background: var(--r5-primary); animation: pulse-dot 0.8s ease infinite; }

      @keyframes pulse-dot {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.3; }
      }

      /* -- Messages -- */
      .messages {
        flex: 1;
        overflow-y: auto;
        padding: var(--r5-space-3);
        display: flex;
        flex-direction: column;
        gap: var(--r5-space-3);
        scroll-behavior: smooth;
      }
      .message {
        display: flex;
        flex-direction: column;
        gap: var(--r5-space-1);
        max-width: 88%;
      }
      .message.user { align-self: flex-end; }
      .message.assistant { align-self: flex-start; }
      .message-bubble {
        padding: var(--r5-space-2) var(--r5-space-3);
        border-radius: var(--r5-radius-lg);
        font-size: var(--r5-text-xs);
        line-height: 1.5;
        white-space: pre-wrap;
        word-break: break-word;
      }
      .user .message-bubble {
        background: var(--r5-primary);
        color: white;
        border-bottom-right-radius: var(--r5-radius-sm);
      }
      .assistant .message-bubble {
        background: var(--r5-surface-muted);
        color: var(--r5-text);
        border-bottom-left-radius: var(--r5-radius-sm);
      }
      .message-meta {
        display: flex;
        align-items: center;
        gap: var(--r5-space-1);
      }
      .user .message-meta { justify-content: flex-end; }
      .timestamp {
        font-size: var(--r5-font-size-badge);
        color: var(--r5-text-muted);
      }

      /* -- Code in messages -- */
      .inline-code {
        display: block;
        margin-top: var(--r5-space-1);
        padding: var(--r5-space-2);
        font-family: var(--r5-font-mono);
        font-size: var(--r5-text-xs);
        background: var(--r5-surface-elevated);
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-md);
        white-space: pre-wrap;
        max-height: 120px;
        overflow-y: auto;
      }

      /* -- Suggestion box -- */
      .suggestion-box {
        margin: 0 var(--r5-space-3) var(--r5-space-2);
        padding: var(--r5-space-2) var(--r5-space-3);
        background: var(--r5-surface-elevated);
        border: 1px solid var(--r5-primary-200);
        border-radius: var(--r5-radius-md);
        animation: slide-up 0.2s var(--r5-ease-default);
      }
      @keyframes slide-up {
        from { opacity: 0; transform: translateY(8px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .suggestion-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--r5-space-1);
      }
      .suggestion-label {
        font-size: var(--r5-text-xs);
        font-weight: 600;
        color: var(--r5-primary);
      }
      .suggestion-code {
        font-family: var(--r5-font-mono);
        font-size: var(--r5-text-xs);
        white-space: pre-wrap;
        margin: var(--r5-space-1) 0;
        max-height: 200px;
        overflow-y: auto;
        padding: var(--r5-space-2);
        background: var(--r5-surface-muted);
        border-radius: var(--r5-radius-sm);
        border: 1px solid var(--r5-border);
      }
      .suggestion-actions {
        display: flex;
        gap: var(--r5-space-2);
        margin-top: var(--r5-space-2);
      }

      /* -- Quick actions -- */
      .quick-actions {
        display: flex;
        flex-wrap: wrap;
        gap: var(--r5-space-1);
        padding: var(--r5-space-2) var(--r5-space-3);
        border-top: 1px solid var(--r5-border);
      }

      /* -- Input row -- */
      .input-row {
        display: flex;
        gap: var(--r5-space-2);
        padding: var(--r5-space-2) var(--r5-space-3);
        border-top: 1px solid var(--r5-border);
        background: var(--r5-surface);
      }
      .input-row input {
        flex: 1;
        padding: var(--r5-space-2);
        font-family: var(--r5-font-sans);
        font-size: var(--r5-text-xs);
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-md);
        background: var(--r5-surface);
        color: var(--r5-text);
      }
      .input-row input:focus {
        outline: 2px solid var(--r5-primary);
        outline-offset: -1px;
      }

      /* -- Empty state -- */
      .empty-state {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: var(--r5-space-3);
        color: var(--r5-text-muted);
        text-align: center;
        padding: var(--r5-space-4);
      }
      .empty-state ark-icon { opacity: 0.4; }
      .quick-prompts {
        display: flex;
        flex-wrap: wrap;
        gap: var(--r5-space-1);
        justify-content: center;
      }

      /* -- Typing indicator -- */
      .typing-indicator {
        display: flex;
        gap: 4px;
        padding: var(--r5-space-2) var(--r5-space-3);
      }
      .typing-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: var(--r5-text-muted);
        animation: typing-bounce 1.2s ease-in-out infinite;
      }
      .typing-dot:nth-child(2) { animation-delay: 0.2s; }
      .typing-dot:nth-child(3) { animation-delay: 0.4s; }
      @keyframes typing-bounce {
        0%, 60%, 100% { transform: translateY(0); }
        30% { transform: translateY(-4px); }
      }
    `,
  ];

  // CRITICAL: hasChanged: generationChanged for controller properties
  @property({ attribute: false, hasChanged: generationChanged }) chatbot!: ChatbotController;
  @property({ attribute: false, hasChanged: generationChanged }) modeler!: ModelerController;

  @state() private _inputText = '';

  @query('.messages') private _messagesEl!: HTMLElement;

  override updated(changedProperties: Map<PropertyKey, unknown>): void {
    super.updated(changedProperties);
    // Auto-scroll to bottom when messages change
    if (this._messagesEl) {
      requestAnimationFrame(() => {
        this._messagesEl.scrollTop = this._messagesEl.scrollHeight;
      });
    }
  }

  override render() {
    return html`
      <div class="panel-header">
        <h3>AI Assistant</h3>
        <div style="display: flex; gap: var(--r5-space-1);">
          ${this.chatbot.messages.length > 0
            ? html`<ark-button size="xs" variant="ghost" @click=${this._onReset}>Clear</ark-button>`
            : nothing}
        </div>
      </div>

      ${this._renderScoreDashboard()}
      ${this._renderPhaseIndicator()}

      ${this.chatbot.messages.length === 0
        ? this._renderEmptyState()
        : this._renderMessages()}

      ${this._renderSuggestion()}
      ${this._renderQuickActions()}
      ${this._renderInput()}
    `;
  }

  // -- Score Dashboard (completeness, quality, trace coverage) -------

  private _renderScoreDashboard() {
    const completeness = this.chatbot.completenessScore;
    const quality = this.chatbot.qualityScore;
    if (completeness === null && quality === null) return nothing;

    const traceCoverage = this._computeTraceCoverage();

    return html`
      <div class="score-dashboard">
        ${completeness !== null ? html`
          <div class="score-row">
            <span class="score-label">Completeness</span>
            <div class="score-track">
              <div
                class="score-fill ${completeness >= 70 ? 'success' : completeness >= 40 ? 'warning' : 'error'}"
                style="width: ${completeness}%"
              ></div>
            </div>
            <span class="score-value">${completeness}%</span>
          </div>
        ` : nothing}

        ${quality !== null ? html`
          <div class="score-row">
            <span class="score-label">Quality</span>
            <span class="quality-badge ${quality.overall >= 70 ? 'good' : quality.overall >= 40 ? 'fair' : 'poor'}">
              ${quality.overall}% - ${quality.overall >= 70 ? 'Good' : quality.overall >= 40 ? 'Fair' : 'Needs Work'}
            </span>
          </div>
          ${quality.issues.length > 0 ? html`
            <div style="font-size: var(--r5-font-size-badge); color: var(--r5-text-muted); padding-left: 82px;">
              ${quality.issues.length} issue${quality.issues.length !== 1 ? 's' : ''} found
            </div>
          ` : nothing}
        ` : nothing}

        ${traceCoverage !== null ? html`
          <div class="score-row">
            <span class="score-label">Trace Cov.</span>
            <div class="score-track">
              <div
                class="score-fill ${traceCoverage >= 80 ? 'success' : traceCoverage >= 50 ? 'warning' : 'error'}"
                style="width: ${traceCoverage}%"
              ></div>
            </div>
            <span class="score-value">${traceCoverage}%</span>
          </div>
        ` : nothing}
      </div>
    `;
  }

  // -- Phase Indicator ------------------------------------------------

  private _renderPhaseIndicator() {
    const labels: Record<string, string> = {
      exploring: 'Exploring your model...',
      ready: 'Ready to help',
      generating: 'Generating response...',
    };

    return html`
      <div class="phase-indicator">
        ${this.chatbot.phase === 'generating'
          ? html`<ark-spinner size="xs"></ark-spinner>`
          : html`<span class="phase-dot ${this.chatbot.phase}"></span>`}
        <span>${labels[this.chatbot.phase]}</span>
      </div>
    `;
  }

  // -- Empty State ---------------------------------------------------

  private _renderEmptyState() {
    const prompts = [
      'Suggest improvements',
      'Analyze quality',
      'Generate a requirement',
      'Add a part definition',
      'Check verification coverage',
      'Explain SysML concepts',
    ];

    return html`
      <div class="empty-state">
        <ark-icon name="message-circle" size="lg"></ark-icon>
        <div>
          <div style="font-weight: 600; margin-bottom: var(--r5-space-1);">SysML AI Assistant</div>
          <div style="font-size: var(--r5-text-xs);">
            I can help analyze your model, suggest improvements, generate SysML code,
            and answer questions about SysML v2 best practices.
          </div>
        </div>
        <div class="quick-prompts">
          ${prompts.map(
            (p) => html`
              <ark-button size="xs" variant="outline" @click=${() => this._sendQuickPrompt(p)}>
                ${p}
              </ark-button>
            `,
          )}
        </div>
      </div>
    `;
  }

  // -- Messages Thread -----------------------------------------------

  private _renderMessages() {
    return html`
      <div class="messages">
        ${this.chatbot.messages.map((msg) => this._renderMessage(msg))}
        ${this.chatbot.isLoading
          ? html`
              <div class="message assistant">
                <div class="message-bubble">
                  <div class="typing-indicator">
                    <span class="typing-dot"></span>
                    <span class="typing-dot"></span>
                    <span class="typing-dot"></span>
                  </div>
                </div>
              </div>
            `
          : nothing}
      </div>
    `;
  }

  private _renderMessage(msg: ChatMessage) {
    return html`
      <div class="message ${msg.role}">
        <div class="message-bubble">
          ${msg.text}
          ${msg.suggestedCode
            ? html`<div class="inline-code">${msg.suggestedCode}</div>`
            : nothing}
        </div>
        <div class="message-meta">
          <span class="timestamp">${this._formatTime(msg.timestamp)}</span>
        </div>
      </div>
    `;
  }

  // -- Suggestion Box ------------------------------------------------

  private _renderSuggestion() {
    if (!this.chatbot.suggestedSourceText) return nothing;
    return html`
      <div class="suggestion-box">
        <div class="suggestion-header">
          <span class="suggestion-label">
            <ark-icon name="terminal" size="xs"></ark-icon>
            Suggested SysML Code
          </span>
        </div>
        <div class="suggestion-code">${this.chatbot.suggestedSourceText}</div>
        <div class="suggestion-actions">
          <ark-button size="xs" variant="primary" @click=${this._onApply}>
            <ark-icon name="check" size="xs"></ark-icon>
            Apply
          </ark-button>
          <ark-button size="xs" variant="ghost" @click=${this._onDismiss}>
            Dismiss
          </ark-button>
        </div>
      </div>
    `;
  }

  // -- Quick Actions -------------------------------------------------

  private _renderQuickActions() {
    if (this.chatbot.messages.length === 0) return nothing;

    const actions = [
      { label: 'Improve', icon: 'sparkles' },
      { label: 'Add Tests', icon: 'list-checks' },
      { label: 'Fix Issues', icon: 'settings' },
    ];

    return html`
      <div class="quick-actions">
        ${actions.map(
          (a) => html`
            <ark-button size="xs" variant="outline" @click=${() => this._sendQuickPrompt(a.label)}>
              <ark-icon name=${a.icon} size="xs"></ark-icon>
              ${a.label}
            </ark-button>
          `,
        )}
      </div>
    `;
  }

  // -- Input Row -----------------------------------------------------

  private _renderInput() {
    return html`
      <div class="input-row">
        <input
          type="text"
          placeholder="Ask about your model..."
          .value=${this._inputText}
          @input=${(e: InputEvent) => (this._inputText = (e.target as HTMLInputElement).value)}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              this._onSend();
            }
          }}
          ?disabled=${this.chatbot.isLoading}
        />
        <ark-button
          size="sm"
          variant="primary"
          ?disabled=${!this._inputText.trim() || this.chatbot.isLoading}
          @click=${this._onSend}
        >
          <ark-icon name="send" size="sm"></ark-icon>
        </ark-button>
      </div>
    `;
  }

  // -- Handlers ------------------------------------------------------

  private _onSend(): void {
    if (!this._inputText.trim()) return;
    const ctx = this.chatbot.buildModelContext(this.modeler);
    this.chatbot.sendMessage(this._inputText, ctx);
    this._inputText = '';
  }

  private _sendQuickPrompt(text: string): void {
    this._inputText = text;
    this._onSend();
  }

  private _onApply(): void {
    const code = this.chatbot.applySuggestion();
    if (code) {
      this.dispatchEvent(
        new CustomEvent('apply-suggestion', {
          detail: { code },
          bubbles: true,
          composed: true,
        }),
      );
    }
  }

  private _onDismiss(): void {
    this.chatbot.dismissSuggestion();
  }

  private _onReset(): void {
    this.chatbot.reset();
  }

  // -- Helpers -------------------------------------------------------

  private _computeTraceCoverage(): number | null {
    const reqs = this.modeler.requirements;
    const verCases = this.modeler.verificationCases;
    if (reqs.length === 0) return null;

    let coveredCount = 0;
    for (const req of reqs) {
      const hasVer = verCases.some(
        (vc) =>
          vc.subject?.ref === req.name ||
          vc.subject?.ref === req.id,
      );
      if (hasVer) coveredCount++;
    }
    return Math.round((coveredCount / reqs.length) * 100);
  }

  private _formatTime(iso: string): string {
    try {
      return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sysml-chatbot-panel': SysmlChatbotPanel;
  }
}
